import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Checkbox, Field, Flex, Input, Text } from '@workwhile/ui'
import {
  supplementBRequestSchema,
  type SupplementBRequest,
} from 'api/user/supplementB'
import { type ListDocument } from 'api/user/uploadI9Document'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { DocumentTitles } from './constants'

type SupplementBFormProps = {
  documents: ListDocument[]
}

export type SupplementBFormHandle = {
  getValues: () => SupplementBRequest
  trigger: () => Promise<boolean>
  reset: () => void
}

const parseNameParts = (name?: string) => {
  const parts = (name ?? '').trim().split(/\s+/)
  if (parts.length === 0 || (parts.length === 1 && !parts[0]))
    return { first: '', last: '', mi: '' }
  if (parts.length === 1) return { first: parts[0], last: '', mi: '' }
  return {
    first: parts[0],
    last: parts[parts.length - 1],
    mi: parts.length > 2 ? parts[1][0] : '',
  }
}

export const SupplementBForm = forwardRef<
  SupplementBFormHandle,
  SupplementBFormProps
>(function SupplementBForm({ documents }, ref) {
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const document = documents[0]
  const nameParts = parseNameParts(document?.name)

  const form = useForm<SupplementBRequest>({
    resolver: zodResolver(supplementBRequestSchema),
    defaultValues: {
      isRehire: false,
      isReverification: false,
      rehireDate: '',
      rehireNewLastName: nameParts.last,
      rehireNewFirstName: nameParts.first,
      rehireNewMiddleInitial: nameParts.mi,
      reverifyDocumentTitle: document
        ? DocumentTitles[document.documentType]
        : '',
      reverifyDocumentNumber: document?.documentNumber ?? '',
      reverifyDocumentExpirationDate: document?.expirationDate ?? '',
      reverifyAuthRepName: '',
      reverifySignedDate: '',
    },
    mode: 'onChange',
  })

  useImperativeHandle(ref, () => ({
    getValues: () => form.getValues(),
    trigger: () => form.trigger(),
    reset: () => {
      form.reset()
      if (filePreview) URL.revokeObjectURL(filePreview)
      setFilePreview(null)
    },
  }))

  const isRehire = form.watch('isRehire')
  const isReverification = form.watch('isReverification')

  return (
    <Box
      p={3}
      mt={2}
      backgroundColor="white"
      borderRadius="4px"
      border="1px solid #ccc"
    >
      <Text fontWeight="bold" fontSize={2} mb={3}>
        Supplement B — Reverification and Rehire
      </Text>

      <Flex flexDirection="column" gap={2}>
        <Controller
          name="isRehire"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field error={fieldState.error?.message}>
              <Checkbox {...field}>Rehire</Checkbox>
            </Field>
          )}
        />

        {isRehire ? (
          <Box ml={3}>
            <Controller
              name="rehireDate"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Date of Rehire" error={fieldState.error?.message}>
                  <Input
                    type="date"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </Field>
              )}
            />

            <Text fontSize={1} color="lightText" mt={2} mb={1}>
              New Name (if applicable)
            </Text>
            <Flex gap={2}>
              <Controller
                name="rehireNewLastName"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Last Name (Family Name)"
                    error={fieldState.error?.message}
                  >
                    <Input {...field} placeholder="Pei" />
                  </Field>
                )}
              />
              <Controller
                name="rehireNewFirstName"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="First Name (Given Name)"
                    error={fieldState.error?.message}
                  >
                    <Input {...field} placeholder="I.M." />
                  </Field>
                )}
              />
              <Box width={80} flexShrink={0}>
                <Controller
                  name="rehireNewMiddleInitial"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field label="MI" error={fieldState.error?.message}>
                      <Input {...field} placeholder="MI" maxLength={1} />
                    </Field>
                  )}
                />
              </Box>
            </Flex>
          </Box>
        ) : null}

        <Controller
          name="isReverification"
          control={form.control}
          render={({ field }) => <Checkbox {...field}>Reverification</Checkbox>}
        />

        {isReverification ? (
          <Box ml={3}>
            <Controller
              name="reverifyDocumentTitle"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Document Title" error={fieldState.error?.message}>
                  <Input {...field} placeholder="e.g. EAD" />
                </Field>
              )}
            />
            <Flex gap={2}>
              <Controller
                name="reverifyDocumentNumber"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Document Number"
                    error={fieldState.error?.message}
                  >
                    <Input {...field} placeholder="e.g. 123456789" />
                  </Field>
                )}
              />
              <Controller
                name="reverifyDocumentExpirationDate"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Expiration Date"
                    error={fieldState.error?.message}
                  >
                    <Input
                      type="date"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </Field>
                )}
              />
            </Flex>
          </Box>
        ) : null}

        <Box mt={2} pt={2} borderTop="1px solid #eee">
          <Controller
            name="reverifyAuthRepName"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Name of Employer or Authorized Representative"
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  placeholder="e.g. John David"
                  style={{ width: '100%' }}
                />
              </Field>
            )}
          />
          <Controller
            name="reverifySignedDate"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Today's Date" error={fieldState.error?.message}>
                <Input
                  type="date"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </Field>
            )}
          />
        </Box>

        <Controller
          name="pdfFile"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field
              label="Signed Supplement B (PDF or Photo)"
              error={fieldState.error?.message}
            >
              {field.value ? (
                <Flex alignItems="center" gap={2}>
                  {filePreview && field.value.type.startsWith('image/') ? (
                    <img
                      src={filePreview}
                      alt="Supplement B preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                  ) : (
                    <Text fontSize={1}>{field.value.name}</Text>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      field.onChange(undefined)
                      if (filePreview) URL.revokeObjectURL(filePreview)
                      setFilePreview(null)
                    }}
                  >
                    Remove
                  </Button>
                </Flex>
              ) : (
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      field.onChange(file)
                      if (file.type.startsWith('image/')) {
                        setFilePreview(URL.createObjectURL(file))
                      }
                    }
                  }}
                />
              )}
            </Field>
          )}
        />
      </Flex>
    </Box>
  )
})
